import CopyToClipboard from 'common/components/CopyToClipboard'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { LoginBody } from 'modules/auth/api/types'
import { useMembers } from 'modules/member/api/selector'
import { MemberResponse } from 'modules/member/api/types'
import AccessDialog from 'modules/member/components/AccessDialog'
import CreateMemberDialog from 'modules/member/components/CreateMemberDialog'
import { useUserInfo } from 'modules/user/api/queries'
import { usePermitToActionFeature } from 'modules/user/api/selector'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { Button, InputAdornment, Tab, Tabs, TextField, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import plusIcon from '@iconify/icons-ant-design/plus'
import { Icon } from '@iconify/react'

import Page from 'components/Page'

import AccessPage from '../AccessPage'
import MemberPage from '../MemberPage'

const ManagementPage = () => {
  const location = useLocation()
  const { t } = useTranslation(['member', 'pond'])
  const theme = useTheme()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 1000)
  const [filteredMember, setFilteredMember] = useState<MemberResponse[] | null>(null)
  const [openMemberDialog, setOpenMemberDialog] = useState(false)

  const [isEditMemberDialog, setIsEditMemberDialog] = useState(false)
  const [selectedMember, setSelectedMember] = useState<MemberResponse>()
  const haveCreatePermission = usePermitToActionFeature([CRUDAction.Create], FeatureKey.User)
  const [activeTab, setActiveTab] = useState(0)
  const [openAccessDialog, setOpenAccessDialog] = useState(false)
  const [isEditAccessDialog, setIsEditAccessDialog] = useState(false)
  const [selectedRole, setSelectedRole] = useState<MemberResponse>()

  const cleanedSearch = debouncedSearch.trim()

  useEffect(() => {
    if (location.pathname.endsWith('/roles')) {
      setActiveTab(1)
    } else {
      setActiveTab(0)
    }
  }, [location.pathname])

  useEffect(() => {
    if (debouncedSearch === '') {
      setFilteredMember(null)
      return
    }
    searchMember()
  }, [debouncedSearch])

  const onSearchChange = (input: string) => {
    setSearch(input)
  }

  function useDebouncedValue<T>(value: T, delay = 1000) {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
      const id = setTimeout(() => setDebounced(value), delay)
      return () => clearTimeout(id)
    }, [value, delay])
    return debounced
  }

  const searchMember = () => {
    if (search !== '') {
      const filteredMembers = members?.filter((el) =>
        el.name?.toLowerCase().includes(search.toLowerCase())
      )

      if (filteredMembers && filteredMembers?.length > 0) {
        setFilteredMember(filteredMembers)
      } else {
        setFilteredMember([])
      }
    } else {
      setFilteredMember(null)
    }
  }

  const handleOpen = () => {
    setOpenMemberDialog(true)
  }

  const handleCloseMemberDialog = (isOpenDetail?: boolean, data?: LoginBody) => {
    setIsEditMemberDialog(false)
    setSelectedMember(undefined)
    setOpenMemberDialog(false)

    if (isOpenDetail) {
      setSelectedMember({
        id: '',
        phone: data?.username,
        organization_id: data?.organization_id,
        password: data?.password,
      })
    }
  }

  const handleCloseAccessDialog = () => {
    setIsEditAccessDialog(false)
    setSelectedRole(undefined)
    setOpenAccessDialog(false)
  }

  const { data: members, isFetching: isMemberFetching, refetch: refetchMembers } = useMembers()
  const { data: userInfo } = useUserInfo()

  return (
    <Page title={activeTab === 0 ? t('staff.list') : t('manage.role')}>
      <Box px={5}>
        <Box
          className="search-member"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 3,
            alignItems: 'center',
            mb: 3,
          }}
        >
          {' '}
          <Typography variant="h3" color="#1C2434">
            {activeTab === 0 ? t('staff.list') : t('manage.role')}
          </Typography>
          {haveCreatePermission && activeTab === 0 && (
            <Button
              onClick={handleOpen}
              color="primary"
              size="large"
              variant="contained"
              sx={{
                width: 'fit-content',
                whiteSpace: 'nowrap',
                height: 'auto',
                display: 'flex',
                gap: '4px',
              }}
            >
              <Icon icon={plusIcon} width={24} height={24} />
              {t('member:add.staff')}
            </Button>
          )}
          {haveCreatePermission && activeTab === 1 && (
            <Button
              onClick={() => {
                setOpenAccessDialog(true)
              }}
              color="primary"
              size="large"
              variant="contained"
              sx={{
                width: 'fit-content',
                whiteSpace: 'nowrap',
                height: 'auto',
                display: 'flex',
                gap: '4px',
              }}
            >
              <Icon icon={plusIcon} width={24} height={24} />
              {t('member:add.role')}
            </Button>
          )}
        </Box>

        {activeTab === 0 && (
          <Box
            className="search-member"
            sx={{
              display: 'flex',
              gap: 3,
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
            }}
          >
            {/* Organization Info */}
            <Box
              sx={{
                background: '#ffffffff',
                borderRadius: '8px',
                border: '1px solid #D9D9D9',
                px: 2,
                py: 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography fontSize={16} fontWeight={400}>
                {t('organization.no')}
              </Typography>
              <Typography fontSize={16} fontWeight={400}>
                {userInfo?.organization_id || '-'}
              </Typography>
              <CopyToClipboard organizationId={userInfo?.organization_id} />
            </Box>

            {/* Search */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder={t('search')}
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchMember()}
                sx={{
                  width: 360,
                  '& .MuiOutlinedInput-root': { borderRadius: 1 },
                  bgcolor: theme.palette.common.white,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ cursor: 'pointer' }}
                      onClick={searchMember}
                    >
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        )}

        {/* Tabs Section */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            textColor="inherit"
            TabIndicatorProps={{
              style: { backgroundColor: theme.palette.primary.main },
            }}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 400,
                fontSize: '18px',
                color: theme.palette.text.primary,
                transition: 'color 0.2s ease, font-weight 0.2s ease',
              },
              '& .MuiTab-root.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            }}
          >
            <Tab label={t('member:staff.list')} />
            <Tab label={t('member:manage.role')} />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <MemberPage
            members={filteredMember ?? members ?? []}
            isMemberFetching={isMemberFetching}
            refetchMembers={refetchMembers}
          />
        )}
        {activeTab === 1 && <AccessPage />}
      </Box>

      {/* Member Dialog */}
      {openMemberDialog && (
        <CreateMemberDialog
          open={openMemberDialog}
          onClose={handleCloseMemberDialog}
          isEdit={isEditMemberDialog}
          initialMember={selectedMember}
          onSuccess={() => refetchMembers()}
        />
      )}

      {/* Access Dialog */}
      {openAccessDialog && (
        <AccessDialog
          open={openAccessDialog}
          onClose={handleCloseAccessDialog}
          isEdit={isEditAccessDialog}
          initialRole={selectedRole}
        />
      )}
    </Page>
  )
}

export default ManagementPage
